import { Fragment, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useItems, useDeleteItem, useCreateItem, useUpdateItem, useItemLogs } from '@/api/use-items';
import { useSceneTree } from '@/api/use-scenes';
import { LOG_TYPE_COLORS } from '@/lib/constants';
// StatusBadge and CopyButton available if needed
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Trash2,
  ChevronDown,
  ChevronRight,
  Plus,
  Upload,
  Filter,
  Archive,
  Play,
  Move,
  X,
  Search,
} from 'lucide-react';
import { cn, formatDate } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';

// ---------------------------------------------------------------------------
// AddItemDialog (kept from original)
// ---------------------------------------------------------------------------

function AddItemDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const createItem = useCreateItem();
  const [form, setForm] = useState({
    text: '',
    item_type: 'word',
    definition: '',
    example: '',
  });

  const handleSubmit = () => {
    if (!form.text.trim()) return;
    createItem.mutate(
      {
        text: form.text.trim(),
        item_type: form.item_type,
        definition: form.definition || undefined,
        example: form.example || undefined,
      },
      {
        onSuccess: () => {
          setForm({ text: '', item_type: 'word', definition: '', example: '' });
          onClose();
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Language Item</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Word / Phrase *</label>
            <Input
              placeholder="e.g. gregarious"
              value={form.text}
              onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Type</label>
            <Select value={form.item_type} onValueChange={(v) => setForm((f) => ({ ...f, item_type: v }))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="word">Word</SelectItem>
                <SelectItem value="phrase">Phrase</SelectItem>
                <SelectItem value="sentence">Sentence</SelectItem>
                <SelectItem value="idiom">Idiom</SelectItem>
                <SelectItem value="phrasal_verb">Phrasal Verb</SelectItem>
                <SelectItem value="collocation">Collocation</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Definition</label>
            <Input
              placeholder="Brief definition"
              value={form.definition}
              onChange={(e) => setForm((f) => ({ ...f, definition: e.target.value }))}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Example Sentence</label>
            <Input
              placeholder="Example usage"
              value={form.example}
              onChange={(e) => setForm((f) => ({ ...f, example: e.target.value }))}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!form.text.trim() || createItem.isPending}>
            {createItem.isPending ? 'Adding...' : 'Add Item'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// CEFR Badge
// ---------------------------------------------------------------------------

const CEFR_COLORS: Record<string, string> = {
  A1: 'bg-green-100 text-green-700 border-green-200',
  A2: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  B1: 'bg-blue-100 text-blue-700 border-blue-200',
  B2: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  C1: 'bg-purple-100 text-purple-700 border-purple-200',
  C2: 'bg-red-100 text-red-700 border-red-200',
};

function CefrBadge({ level }: { level: string | null }) {
  if (!level) return <span className="text-xs text-muted-foreground">--</span>;
  const colorClass = CEFR_COLORS[level] ?? 'bg-muted text-muted-foreground';
  return (
    <span className={cn('inline-block px-1.5 py-0.5 rounded text-[10px] font-bold border', colorClass)}>
      {level}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Source display helpers
// ---------------------------------------------------------------------------

const SOURCE_CONFIG: Record<string, { icon: string; label: string; color: string }> = {
  book: { icon: '📖', label: 'Book', color: 'text-amber-600' },
  training: { icon: '🎯', label: 'Training', color: 'text-indigo-600' },
  manual: { icon: '✏️', label: 'Manual', color: 'text-slate-600' },
  discovered: { icon: '💡', label: 'Discovered', color: 'text-emerald-600' },
};

function SourceDisplay({ sourceType, sourceBook, sourceRef }: {
  sourceType: string; sourceBook: string | null; sourceRef: string | null;
}) {
  const config = SOURCE_CONFIG[sourceType] ?? SOURCE_CONFIG.manual;
  const detail = sourceBook || sourceRef;
  return (
    <div className="flex items-center gap-1.5 text-xs">
      <span className="flex-shrink-0">{config.icon}</span>
      <div className="flex flex-col">
        <span className={cn('font-medium', config.color)}>{config.label}</span>
        {detail && (
          <span className="text-muted-foreground truncate max-w-[100px]">
            {[sourceBook, sourceRef].filter(Boolean).join(' · ')}
          </span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status dot color helper
// ---------------------------------------------------------------------------

const STATUS_DOT: Record<string, string> = {
  new: 'bg-blue-500',
  learning: 'bg-amber-500',
  recognized: 'bg-emerald-500',
  mastered: 'bg-violet-500',
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ITEM_TYPES = ['All', 'Word', 'Phrase', 'Sentence', 'Idiom', 'Phrasal Verb', 'Collocation'] as const;
const TYPE_API_MAP: Record<string, string> = {
  'Phrasal Verb': 'phrasal_verb',
};
const STATUSES = ['All', 'new', 'learning', 'recognized', 'mastered'] as const;
const CEFR_LEVELS = ['All', 'A1', 'A2', 'B1', 'B2', 'C1', 'C2'] as const;
const SOURCE_TYPES = ['All', 'book', 'training', 'manual', 'discovered'] as const;
const PAGE_SIZE = 15;

interface Filter {
  type?: string;
  status?: string;
  cefr_level?: string;
  source_type?: string;
  scene?: string;
  overdue?: boolean;
  deleted?: boolean;
  offset: number;
  limit: number;
}

// ---------------------------------------------------------------------------
// Helper: check if a date is overdue
// ---------------------------------------------------------------------------

function isOverdue(dateStr: string | null): boolean {
  if (!dateStr) return false;
  return new Date(dateStr) < new Date();
}

// ---------------------------------------------------------------------------
// Helper: highlight word in example sentence
// ---------------------------------------------------------------------------

function highlightWord(sentence: string, word: string) {
  const idx = sentence.toLowerCase().indexOf(word.toLowerCase());
  if (idx === -1) return <span>{sentence}</span>;
  return (
    <span>
      {sentence.slice(0, idx)}
      <strong className="text-foreground">{sentence.slice(idx, idx + word.length)}</strong>
      {sentence.slice(idx + word.length)}
    </span>
  );
}

// ---------------------------------------------------------------------------
// ExpandedRow (uses real item logs from API)
// ---------------------------------------------------------------------------

const QUALITY_LABELS: Record<number, { label: string; color: string }> = {
  5: { label: 'Perfect', color: 'bg-emerald-500' },
  4: { label: 'Good', color: 'bg-blue-500' },
  3: { label: 'OK', color: 'bg-amber-500' },
  2: { label: 'Hard', color: 'bg-orange-500' },
  1: { label: 'Again', color: 'bg-red-500' },
  0: { label: 'Fail', color: 'bg-red-700' },
};

function ExpandedRow({
  item,
  onStatusChange,
  onSm2Toggle,
  cardNameMap,
}: {
  item: {
    id: string;
    text: string;
    definition: string | null;
    example: string | null;
    notes: string | null;
    status: string;
    ease_factor: number;
    interval: number;
    review_count: number;
    sm2_enabled: number;
    card_id: string | null;
    source_type: string;
    source_book: string | null;
    source_ref: string | null;
  };
  onStatusChange: (status: string) => void;
  onSm2Toggle: (value: number) => void;
  cardNameMap: Map<string, { name: string; sid: string | null }>;
}) {
  const nav = useNavigate();
  const { data: logs } = useItemLogs(item.id);
  const statusOptions = ['new', 'learning', 'mastered', 'relearn'] as const;

  return (
    <tr className="bg-muted/10 border-t">
      <td colSpan={8} className="px-6 py-5">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left side: definition, example, notes */}
          <div className="lg:col-span-2 space-y-4">
            {item.definition && (
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                  Definition
                </h4>
                <p className="text-sm leading-relaxed">{item.definition}</p>
              </div>
            )}
            {item.example && (
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                  Example Sentence
                </h4>
                <blockquote className="border-l-4 border-indigo-500 pl-3 py-1 text-sm italic text-muted-foreground">
                  {highlightWord(item.example, item.text)}
                </blockquote>
              </div>
            )}
            {item.notes && (
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                  Notes
                </h4>
                <p className="text-xs text-muted-foreground">{item.notes}</p>
              </div>
            )}
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                Source
              </h4>
              <SourceDisplay
                sourceType={item.source_type}
                sourceBook={item.source_book}
                sourceRef={item.source_ref}
              />
            </div>
            {/* Scene Card Link */}
            {item.card_id && cardNameMap.get(item.card_id) && (
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                  Scene
                </h4>
                <button
                  onClick={() => nav(`/scenes?card=${item.card_id}`)}
                  className="text-xs text-indigo-600 hover:underline dark:text-indigo-400"
                >
                  {cardNameMap.get(item.card_id)!.name}
                </button>
              </div>
            )}
          </div>

          {/* Right side: memory engine, status override, recent logs */}
          <div className="space-y-4">
            {/* Memory Engine Card */}
            <Card className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Memory Engine <span className="text-muted-foreground/60">(SM-2)</span>
                </h4>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-muted-foreground">{item.sm2_enabled ? 'ON' : 'OFF'}</span>
                  <Switch
                    checked={item.sm2_enabled !== 0}
                    onCheckedChange={(checked) => onSm2Toggle(checked ? 1 : 0)}
                  />
                </div>
              </div>
              <div className={cn('grid grid-cols-3 gap-2', !item.sm2_enabled && 'opacity-40')}>
                <div className="bg-muted/50 rounded-lg p-2 text-center">
                  <div className="text-lg font-bold">{item.ease_factor.toFixed(2)}</div>
                  <div className="text-[10px] uppercase text-muted-foreground">Ease</div>
                </div>
                <div className="bg-muted/50 rounded-lg p-2 text-center">
                  <div className="text-lg font-bold">{item.interval}d</div>
                  <div className="text-[10px] uppercase text-muted-foreground">Interval</div>
                </div>
                <div className="bg-muted/50 rounded-lg p-2 text-center">
                  <div className="text-lg font-bold">{item.review_count}</div>
                  <div className="text-[10px] uppercase text-muted-foreground">Reviews</div>
                </div>
              </div>
            </Card>

            {/* Status Override */}
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                Status Override
              </h4>
              <div className="flex gap-1.5">
                {statusOptions.map((s) => (
                  <button
                    key={s}
                    onClick={() => onStatusChange(s === 'relearn' ? 'learning' : s)}
                    className={cn(
                      'px-2.5 py-1 rounded text-xs font-medium transition-colors',
                      item.status === s || (s === 'relearn' && false)
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80',
                    )}
                  >
                    {s.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>

            {/* Recent Logs */}
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                Recent Logs
              </h4>
              <div className="space-y-1.5">
                {(logs ?? []).length === 0 ? (
                  <p className="text-xs text-muted-foreground">No logs yet</p>
                ) : (
                  (logs ?? []).map((log) => {
                    const q = QUALITY_LABELS[log.quality ?? 0] ?? QUALITY_LABELS[0];
                    const logTypeColor = LOG_TYPE_COLORS[log.log_type] ?? 'bg-gray-100 text-gray-700';
                    return (
                      <button
                        key={log.log_id}
                        onClick={() => nav(`/logs?id=${log.log_id}`)}
                        className="flex items-center gap-2 text-xs w-full text-left hover:bg-muted/50 rounded px-1 py-0.5 -mx-1 transition-colors"
                      >
                        <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-medium capitalize', logTypeColor)}>
                          {log.log_type}
                        </span>
                        <span className={cn('w-2 h-2 rounded-full flex-shrink-0', q.color)} />
                        <span className="text-muted-foreground">
                          <span className="font-medium text-foreground">{q.label}</span>
                        </span>
                        <span className="ml-auto text-muted-foreground/60">{formatDate(log.date)}</span>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Pagination with page numbers
// ---------------------------------------------------------------------------

function PagePagination({
  total,
  offset,
  limit,
  onOffsetChange,
}: {
  total: number;
  offset: number;
  limit: number;
  onOffsetChange: (offset: number) => void;
}) {
  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.ceil(total / limit);
  const showEnd = Math.min(offset + limit, total);

  // Build page numbers to show
  const pages = useMemo(() => {
    const result: (number | '...')[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) result.push(i);
    } else {
      result.push(1);
      if (currentPage > 3) result.push('...');
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) result.push(i);
      if (currentPage < totalPages - 2) result.push('...');
      result.push(totalPages);
    }
    return result;
  }, [currentPage, totalPages]);

  return (
    <div className="flex items-center justify-between py-4">
      <span className="text-sm text-muted-foreground">
        Showing {offset + 1}-{showEnd} of {total} items
      </span>
      <div className="flex items-center gap-1">
        {pages.map((p, i) =>
          p === '...' ? (
            <span key={`dots-${i}`} className="px-2 text-sm text-muted-foreground">
              ...
            </span>
          ) : (
            <button
              key={p}
              onClick={() => onOffsetChange((p - 1) * limit)}
              className={cn(
                'min-w-[32px] h-8 rounded text-sm font-medium transition-colors',
                p === currentPage
                  ? 'bg-primary text-primary-foreground'
                  : 'hover:bg-muted text-muted-foreground',
              )}
            >
              {p}
            </button>
          ),
        )}
      </div>
    </div>
  );
}

// ===========================================================================
// Main Page Component
// ===========================================================================

export default function Items() {
  const [filter, setFilter] = useState<Filter>({ offset: 0, limit: PAGE_SIZE });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Build card name map for scene badge display
  const { data: sceneTree } = useSceneTree();
  const cardNameMap = useMemo(() => {
    const map = new Map<string, { name: string; sid: string | null }>();
    for (const group of sceneTree ?? []) {
      for (const scene of group.scenes) {
        for (const card of scene.cards) {
          map.set(card.id, { name: card.name, sid: card.sid });
        }
      }
    }
    return map;
  }, [sceneTree]);

  const { data, isLoading } = useItems({
    status: filter.status,
    type: filter.type,
    overdue: filter.overdue,
    deleted: filter.deleted,
    source_type: filter.source_type,
    cefr_level: filter.cefr_level,
    offset: filter.offset,
    limit: filter.limit,
  });
  const deleteItem = useDeleteItem();
  const updateItem = useUpdateItem();

  const rawItems = data?.data ?? [];
  const pagination = data?.pagination;

  // Client-side search filter (Phase B will add backend search)
  const items = useMemo(() => {
    if (!searchQuery.trim()) return rawItems;
    const q = searchQuery.toLowerCase();
    return rawItems.filter(
      (item) =>
        item.text.toLowerCase().includes(q) ||
        (item.definition ?? '').toLowerCase().includes(q) ||
        (item.example ?? '').toLowerCase().includes(q),
    );
  }, [rawItems, searchQuery]);

  const totalCount = searchQuery.trim() ? items.length : (pagination?.total ?? 0);

  // ---- Selection helpers ----

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === items.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map((i) => i.id)));
    }
  };

  // ---- Filter helpers ----

  const setTypeFilter = (type: string) => {
    setFilter((f) => ({
      ...f,
      type: type === 'All' ? undefined : (TYPE_API_MAP[type] ?? type.toLowerCase()),
      offset: 0,
    }));
  };

  const setStatusFilter = (status: string) => {
    setFilter((f) => ({ ...f, status: status === 'All' ? undefined : status, offset: 0 }));
  };

  const setCefrFilter = (level: string) => {
    setFilter((f) => ({ ...f, cefr_level: level === 'All' ? undefined : level, offset: 0 }));
  };

  const setSourceTypeFilter = (source: string) => {
    setFilter((f) => ({ ...f, source_type: source === 'All' ? undefined : source, offset: 0 }));
  };

  // ---- Bulk actions ----

  const handleBulkLearnNow = () => {
    selectedIds.forEach((id) => updateItem.mutate({ id, status: 'learning' }));
    setSelectedIds(new Set());
  };

  const handleBulkArchive = () => {
    selectedIds.forEach((id) => deleteItem.mutate(id));
    setSelectedIds(new Set());
  };

  const handleBulkMove = () => {
    toast({ title: 'Coming soon', description: 'Move functionality will be available in Phase B.' });
  };

  const handleBulkDelete = () => {
    selectedIds.forEach((id) => deleteItem.mutate(id));
    setSelectedIds(new Set());
  };

  // ---- Status update for single item ----

  const handleStatusChange = (id: string, status: string) => {
    updateItem.mutate({ id, status });
  };

  // ---- Active type chip ----

  const activeType = (() => {
    if (!filter.type) return 'All';
    // Reverse lookup from API value to display label
    const reverseMap: Record<string, string> = { phrasal_verb: 'Phrasal Verb' };
    return reverseMap[filter.type] ?? filter.type.charAt(0).toUpperCase() + filter.type.slice(1);
  })();

  return (
    <div className="space-y-5">
      {/* ================================================================ */}
      {/* Header */}
      {/* ================================================================ */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight">Language Items</h1>
            {pagination && (
              <Badge variant="secondary" className="text-xs font-medium">
                {totalCount} items
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button variant="outline" disabled className="opacity-60">
                      <Upload className="w-4 h-4 mr-2" />
                      Bulk Import
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Use the MCP Claude integration to bulk import items</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Button onClick={() => setAddOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Item
            </Button>
            <AddItemDialog open={addOpen} onClose={() => setAddOpen(false)} />
          </div>
        </div>
        <p className="text-sm text-muted-foreground max-w-2xl">
          Manage your personalized lexicon. Track word mastery, review schedules, and contextual
          learning scenes.
        </p>

        {/* Search bar */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search items by text, definition, or example..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-9"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* ================================================================ */}
      {/* Filter bar */}
      {/* ================================================================ */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          {/* Filter icon */}
          <Filter className="w-4 h-4 text-muted-foreground" />

          {/* Type chips */}
          <div className="flex gap-1">
            {ITEM_TYPES.map((type) => (
              <button
                key={type}
                onClick={() => setTypeFilter(type)}
                className={cn(
                  'px-3 py-1.5 rounded-full text-xs font-medium transition-colors',
                  activeType === type
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80',
                )}
              >
                {type}
              </button>
            ))}
          </div>

          {/* Status dropdown */}
          <Select value={filter.status ?? 'All'} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-32 h-8 text-xs">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s === 'All' ? 'Status' : s.charAt(0).toUpperCase() + s.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* CEFR Level dropdown */}
          <Select value={filter.cefr_level ?? 'All'} onValueChange={setCefrFilter}>
            <SelectTrigger className="w-28 h-8 text-xs">
              <SelectValue placeholder="CEFR" />
            </SelectTrigger>
            <SelectContent>
              {CEFR_LEVELS.map((l) => (
                <SelectItem key={l} value={l}>
                  {l === 'All' ? 'CEFR Level' : l}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Source Type dropdown */}
          <Select value={filter.source_type ?? 'All'} onValueChange={setSourceTypeFilter}>
            <SelectTrigger className="w-32 h-8 text-xs">
              <SelectValue placeholder="Source" />
            </SelectTrigger>
            <SelectContent>
              {SOURCE_TYPES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s === 'All' ? 'Source' : s.charAt(0).toUpperCase() + s.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Right side toggles */}
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-xs font-medium cursor-pointer">
            <span className="relative flex items-center gap-1.5">
              {filter.overdue && (
                <span className="absolute -left-3 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-red-500" />
              )}
              OVERDUE
            </span>
            <Switch
              checked={!!filter.overdue}
              onCheckedChange={(v) => setFilter((f) => ({ ...f, overdue: v, offset: 0 }))}
            />
          </label>
          <label className="flex items-center gap-2 text-xs font-medium cursor-pointer">
            DELETED
            <Switch
              checked={!!filter.deleted}
              onCheckedChange={(v) => setFilter((f) => ({ ...f, deleted: v, offset: 0 }))}
            />
          </label>
        </div>
      </div>

      {/* ================================================================ */}
      {/* Table */}
      {/* ================================================================ */}
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="w-10 px-3 py-3">
                <Checkbox
                  checked={items.length > 0 && selectedIds.size === items.length}
                  onCheckedChange={toggleAll}
                />
              </th>
              <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Text
              </th>
              <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Type
              </th>
              <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Status
              </th>
              <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Level
              </th>
              <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Next Review
              </th>
              <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Source
              </th>
              <th className="w-10 px-3 py-3" />
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="border-t">
                  <td className="px-3 py-3" colSpan={8}>
                    <div className="h-5 bg-muted rounded animate-pulse" />
                  </td>
                </tr>
              ))
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-3 py-16 text-center text-muted-foreground">
                  No items found. Add your first language item to get started.
                </td>
              </tr>
            ) : (
              items.map((item) => {
                const overdue = isOverdue(item.next_review);
                return (
                  <Fragment key={item.id}>
                    <tr
                      className={cn(
                        'border-t hover:bg-muted/30 cursor-pointer transition-colors',
                        selectedIds.has(item.id) && 'bg-primary/5',
                      )}
                      onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
                    >
                      {/* Checkbox */}
                      <td
                        className="px-3 py-3"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleSelect(item.id);
                        }}
                      >
                        <Checkbox
                          checked={selectedIds.has(item.id)}
                          onCheckedChange={() => toggleSelect(item.id)}
                        />
                      </td>

                      {/* Text */}
                      <td className="px-3 py-3">
                        <div className="font-medium">{item.text}</div>
                        {item.item_type === 'idiom' && item.definition && (
                          <div className="text-xs text-muted-foreground mt-0.5 truncate max-w-[200px]">
                            {item.definition}
                          </div>
                        )}
                      </td>

                      {/* Type badge */}
                      <td className="px-3 py-3">
                        <Badge
                          variant="outline"
                          className="text-[10px] font-semibold uppercase tracking-wider"
                        >
                          {item.item_type}
                        </Badge>
                      </td>

                      {/* Status (colored dot + text) */}
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1.5">
                          <span
                            className={cn(
                              'w-2 h-2 rounded-full flex-shrink-0',
                              STATUS_DOT[item.status] ?? 'bg-gray-400',
                            )}
                          />
                          <span className="text-xs capitalize">{item.status}</span>
                        </div>
                      </td>

                      {/* CEFR Level */}
                      <td className="px-3 py-3">
                        <CefrBadge level={item.cefr_level} />
                      </td>

                      {/* Next Review (red if overdue) */}
                      <td className="px-3 py-3">
                        {item.next_review ? (
                          <span
                            className={cn(
                              'text-xs',
                              overdue ? 'text-red-600 font-medium' : 'text-muted-foreground',
                            )}
                          >
                            {formatDate(item.next_review)}
                            {overdue && (
                              <span className="ml-1.5 inline-block text-red-500 text-[10px] font-semibold bg-red-50 px-1.5 py-0.5 rounded">
                                OVERDUE
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">--</span>
                        )}
                      </td>

                      {/* Source */}
                      <td className="px-3 py-3">
                        <SourceDisplay
                          sourceType={item.source_type}
                          sourceBook={item.source_book}
                          sourceRef={item.source_ref}
                        />
                      </td>

                      {/* Expand arrow */}
                      <td className="px-3 py-3">
                        {expandedId === item.id ? (
                          <ChevronDown className="w-4 h-4 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        )}
                      </td>
                    </tr>

                    {/* Expanded row */}
                    {expandedId === item.id && (
                      <ExpandedRow
                        key={`${item.id}-expanded`}
                        item={item}
                        onStatusChange={(status) => handleStatusChange(item.id, status)}
                        onSm2Toggle={(value) => updateItem.mutate({ id: item.id, sm2_enabled: value })}
                        cardNameMap={cardNameMap}
                      />
                    )}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ================================================================ */}
      {/* Pagination */}
      {/* ================================================================ */}
      {pagination && totalCount > filter.limit && (
        <PagePagination
          total={totalCount}
          offset={filter.offset}
          limit={filter.limit}
          onOffsetChange={(offset) => setFilter((f) => ({ ...f, offset }))}
        />
      )}

      {/* ================================================================ */}
      {/* Bulk action bar */}
      {/* ================================================================ */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-foreground text-background rounded-xl px-6 py-3 flex items-center gap-3 shadow-2xl z-50">
          <span className="text-sm font-semibold mr-2">{selectedIds.size} Items selected</span>

          <Button
            size="sm"
            variant="secondary"
            className="gap-1.5 text-xs"
            onClick={handleBulkLearnNow}
          >
            <Play className="w-3.5 h-3.5" />
            Learn Now
          </Button>

          <Button
            size="sm"
            variant="secondary"
            className="gap-1.5 text-xs"
            onClick={handleBulkMove}
          >
            <Move className="w-3.5 h-3.5" />
            Move
          </Button>

          <Button
            size="sm"
            variant="secondary"
            className="gap-1.5 text-xs"
            onClick={handleBulkArchive}
          >
            <Archive className="w-3.5 h-3.5" />
            Archive
          </Button>

          <Button
            size="sm"
            variant="destructive"
            className="gap-1.5 text-xs"
            onClick={handleBulkDelete}
          >
            <Trash2 className="w-3.5 h-3.5" />
            Delete
          </Button>

          <button
            className="ml-2 text-background/60 hover:text-background transition-colors"
            onClick={() => setSelectedIds(new Set())}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
